import { colors } from '@dixa/lib-shared-tokens';
import memoizeOne from 'memoize-one';
import { getSeriesValue } from '../../chartInstrument/utils/dataUtils';
/** The default heatmap sequential color scheme. */
export const heatmapColorScheme = [
    '100',
    '200',
    '400',
    '500',
    '700',
].map((shade) => colors.purple[shade]);
/**
 * Returns the styles to be used by a specific heatmap cell, according to the
 * percentage point where the cell value lies in the min and max range of the
 * heatmap data.
 *
 * The color scheme is a sequential list of 5 colors, and the resulting
 * background color will be the one at the index matching one of the 5
 * percentage intervals where the cell value lies. The percentage intervals
 * start at 0 and exclude trailing numbers: 0-20, 20-40, 40-60, 60-80, 80-100.
 *
 * The text color is either `primary` or `white`, depending on the background
 * color.
 *
 * @example
 *   19% --> percentage interval 0-20 --> color at index 0
 *   25% --> percentage interval 20-40 --> color at index 1
 */
export const getHeatmapCellStyles = memoizeOne((percentage) => {
    var _a;
    if (percentage === null) {
        return {
            backgroundColor: colors.neutral['000'],
            color: 'tertiary',
        };
    }
    const shadeIndex = Math.min(4, Math.floor(Math.min(percentage, 100) * (5 / 100)));
    return {
        backgroundColor: (_a = heatmapColorScheme === null || heatmapColorScheme === void 0 ? void 0 : heatmapColorScheme[shadeIndex]) !== null && _a !== void 0 ? _a : colors.neutral['000'],
        color: shadeIndex >= 3 ? 'white' : 'primary',
    };
});
/**
 * Returns whether the given value is a positive number.
 *
 * @example
 *   isPositiveNumber(1); // true
 *   isPositiveNumber(-1); // false
 *   isPositiveNumber(null); // false
 */
function isPositiveNumber(maybeNumber) {
    return (typeof maybeNumber === 'number' &&
        !Number.isNaN(maybeNumber) &&
        maybeNumber >= 0);
}
/**
 * Returns the smallest and largest numbers of the given heatmap series data.
 *
 * @example
 *   extractMinMax([-1, 0, null, 2, 3, 4, 5]); // { min: 0, max: 5 }
 */
export const extractMinMax = (series) => {
    const numbers = series
        .flatMap(({ data }) => data.map((item) => getSeriesValue(item).value))
        .filter(isPositiveNumber);
    const min = Math.min(...numbers);
    const max = Math.max(...numbers);
    return { min, max };
};
/**
 * Returns the rounded percentage point where `value` lies in the given `min`
 * and `max` range.
 *
 * @example
 *   calculatePercentagePoint(10, { min: 0, max: 100 }); // 10%
 *   calculatePercentagePoint(10, { min: 0, max: 30 }); // 33%
 *   calculatePercentagePoint(10, { min: 10, max: 10 }); // 100%
 */
export const calculatePercentagePoint = (value, { min, max }) => {
    return min === max ? 100 : Math.round((100 * (value - min)) / (max - min));
};
